/* import __COLOCATED_TEMPLATE__ from './start-list.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import ENV from 'bullitttime-event/config/environment';

// Base fields for mapping dropdown (fields without custom labels)
const BASE_FIELDS = [
	{ value: '', label: '-- Not mapped --' },
	{ value: 'bib', label: 'Bib Number', required: true },
	{ value: 'seedGroup', label: 'Seed Group' },
	{ value: 'startTime', label: 'Start Time', required: true },
	{ value: 'className', label: 'Class', required: true },
	{ value: 'firstName', label: 'First Name' },
	{ value: 'lastName', label: 'Last Name' },
	{ value: 'DOB', label: 'Date of Birth' },
	{ value: 'gender', label: 'Gender', required: true },
	{ value: 'town', label: 'Town/City' },
	{ value: 'state', label: 'State/Province' },
	{ value: 'country', label: 'Country' },
	{ value: 'club', label: 'Club/Team' }
];

// Fields that can have custom labels from the event
const CUSTOMIZABLE_FIELDS = ['id1', 'id2', 'id3', 'pts1', 'pts2', 'pts3', 'pts4', 'pts5', 'text1', 'text2', 'text3'];

export default class StartListUploadComponent extends Component
{
	@service store;
	@service errorManager;

	// Step tracking: 'select', 'mapping', 'results'
	@tracked currentStep = 'select';

	// Upload options
	@tracked shouldUpdate = true;
	@tracked shouldInsert = true;
	@tracked shouldDrop = false;

	// Analysis results
	@tracked sessionId = null;
	@tracked headers = [];
	@tracked mapping = {};
	@tracked unmappedColumns = [];
	@tracked previewRows = [];
	@tracked totalRows = 0;
	@tracked requiredFields = [];
	@tracked missingRequired = [];
	@tracked customFieldLabels = {};

	// Processing state
	@tracked isAnalyzing = false;
	@tracked isConfirming = false;

	// Final results
	@tracked updated = "";
	@tracked inserted = "";
	@tracked dropped = "";
	@tracked errors = {};
	@tracked classesCreated = [];

	// Build available fields dynamically using custom labels from event
	get availableFields() {
		const fields = [...BASE_FIELDS];

		// Add customizable fields with their custom labels (if set)
		for (const fieldName of CUSTOMIZABLE_FIELDS) {
			const customLabel = this.customFieldLabels[fieldName];
			fields.push({
				value: fieldName,
				label: customLabel || fieldName.toUpperCase()
			});
		}

		return fields;
	}

	get baseUrl() {
		return ENV.eventBackendProtocol + "://" + ENV.eventBackendHost + ":" + ENV.eventBackendPort + (ENV.eventBackendNamespace ? "/" + ENV.eventBackendNamespace : "");
	}

	get columnMappingList() {
		// Create an array of column objects for the template
		return this.headers.map((header, index) => {
			// Find if this column is mapped to a field
			let mappedField = '';
			for (const [field, fieldMapping] of Object.entries(this.mapping)) {
				if (fieldMapping.columnIndex === index) {
					mappedField = field;
					break;
				}
			}

			return {
				index,
				header,
				mappedField,
				isRequired: this.requiredFields.includes(mappedField),
				isMissing: !mappedField && this.unmappedColumns.some(u => u.columnIndex === index)
			};
		});
	}

	get hasMissingRequired() {
		return this.missingRequired.length > 0;
	}

	get canConfirm() {
		return !this.hasMissingRequired && !this.isConfirming;
	}

	@action
	toggleShouldInsert(event)
	{
		this.shouldInsert = event.target.checked;
	}

	@action
	toggleShouldUpdate(event)
	{
		this.shouldUpdate = event.target.checked;
	}

	@action
	toggleShouldDrop(event)
	{
		this.shouldDrop = event.target.checked;
	}

	@action
	async analyzeFile(file)
	{
		this.isAnalyzing = true;

		try {
			const url = this.baseUrl + "/events/" + this.args.eventId + "/analyzeStartList";
			const fetchResponse = await file.upload(url);

			// Read the full response body (handles chunked responses properly)
			const responseText = await fetchResponse.text();
			const response = JSON.parse(responseText);

			if (response.error) {
				this.errorManager.addError(response.error, { clearable: true, type: 'validation' });
				this.isAnalyzing = false;
				return;
			}

			// Store analysis results
			this.sessionId = response.sessionId;
			this.headers = response.headers;
			this.mapping = response.mapping;
			this.unmappedColumns = response.unmappedColumns || [];
			this.previewRows = response.preview?.sampleRows || [];
			this.totalRows = response.preview?.totalRows || 0;
			this.requiredFields = response.requiredFields || [];
			this.missingRequired = response.missingRequired || [];
			this.customFieldLabels = response.customFieldLabels || {};

			// Move to mapping step
			this.currentStep = 'mapping';
		} catch (error) {
			this.errorManager.addError('Failed to analyze file: ' + error.message, { clearable: true, type: 'network' });
		}

		this.isAnalyzing = false;
	}

	@action
	updateColumnMapping(columnIndex, event) {
		const newField = event.target.value;
		const oldMapping = { ...this.mapping };

		// Remove any existing mapping for this column index
		for (const [field, fieldMapping] of Object.entries(oldMapping)) {
			if (fieldMapping.columnIndex === columnIndex) {
				delete oldMapping[field];
				break;
			}
		}

		// If selecting a new field, remove any existing mapping for that field
		// and add the new mapping
		if (newField) {
			delete oldMapping[newField];
			oldMapping[newField] = {
				columnIndex,
				confidence: 'user',
				headerName: this.headers[columnIndex]
			};
		}

		this.mapping = oldMapping;

		// Recalculate missing required
		this.missingRequired = this.requiredFields.filter(field => !this.mapping[field]);
	}

	@action
	async confirmUpload() {
		this.isConfirming = true;

		try {
			const url = this.baseUrl + "/events/" + this.args.eventId + "/confirmStartList";

			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					sessionId: this.sessionId,
					mapping: this.mapping,
					options: {
						insert: this.shouldInsert,
						update: this.shouldUpdate,
						drop: this.shouldDrop
					}
				})
			});

			const result = await response.json();

			if (!response.ok) {
				this.errorManager.addError(result.error || 'Failed to process start list', { clearable: true, type: 'validation' });
				this.isConfirming = false;
				return;
			}

			// Store results
			this.inserted = result.numRecordsInserted;
			this.updated = result.numRecordsUpdated;
			this.dropped = result.numRecordsDropped;
			this.errors = result.errors;
			this.classesCreated = result.classesCreated || [];

			// Refresh store data
			const store = this.store;

			var sTRs = store.peekAll('Start-Timing-Record');
			sTRs.forEach((modelInstance) => modelInstance.unloadRecord());

			var entries = store.peekAll('Entry');
			entries.forEach((modelInstance) => modelInstance.unloadRecord());

			// Reload classes if any were created
			if (this.classesCreated.length > 0) {
				var classes = store.peekAll('Class');
				classes.forEach((modelInstance) => modelInstance.unloadRecord());
			}

			var events = store.peekAll('Event');
			events.forEach((modelInstance) =>
			{
				store.findRecord('Event', modelInstance.id, { reload: true });
			});

			// Move to results step
			this.currentStep = 'results';
		} catch (error) {
			this.errorManager.addError('Failed to process start list: ' + error.message, { clearable: true, type: 'network' });
		}

		this.isConfirming = false;
	}

	@action
	startOver() {
		// Reset to initial state
		this.currentStep = 'select';
		this.sessionId = null;
		this.headers = [];
		this.mapping = {};
		this.unmappedColumns = [];
		this.previewRows = [];
		this.totalRows = 0;
		this.missingRequired = [];
		this.customFieldLabels = {};
		this.inserted = "";
		this.updated = "";
		this.dropped = "";
		this.errors = {};
		this.classesCreated = [];
	}

	@action
	goBack() {
		if (this.currentStep === 'mapping') {
			this.startOver();
		} else if (this.currentStep === 'results') {
			this.currentStep = 'mapping';
		}
	}
}
